import React, { useEffect, useRef, useState, useCallback } from "react";
import timelineDay1 from "./data/timeline.json";
import timelineDay2 from "./data/timeline-day2.json";
import "./EventTimelinePage.css";

const CATEGORIES = {
  // Technical/Coding - Cyan
  code: { color: "#00f2fe", label: "Coding" },
  psychology: { color: "#00f2fe", label: "Technical" },
  bug_report: { color: "#00f2fe", label: "Debug" },
  sync: { color: "#00f2fe", label: "Coding" },
  computer: { color: "#00f2fe", label: "Hardware" },
  keyboard: { color: "#00f2fe", label: "Speed" },
  security: { color: "#00f2fe", label: "Security" },
  memory: { color: "#00f2fe", label: "Challenge" },

  // Creative - Pink/Magenta
  palette: { color: "#f093fb", label: "Design" },
  image: { color: "#f093fb", label: "Visual" },
  movie: { color: "#f093fb", label: "Media" },
  music_note: { color: "#f093fb", label: "Audio" },

  // Workshop/Learning - Purple
  build: { color: "#a855f7", label: "Workshop" },
  school: { color: "#a855f7", label: "Learning" },

  // Fun/Gaming - Orange
  quiz: { color: "#f97316", label: "Quiz" },
  sports_esports: { color: "#f97316", label: "Gaming" },
  sports_cricket: { color: "#f97316", label: "Sports" },
  search: { color: "#f97316", label: "Mystery" },
  lock_open: { color: "#f97316", label: "Puzzle" },
  extension: { color: "#f97316", label: "Puzzle" },

  // Business - Green
  campaign: { color: "#22c55e", label: "Business" },

  // Special Events - Gold
  rocket_launch: { color: "#fbbf24", label: "Ceremony" },
  restaurant: { color: "#94a3b8", label: "Break" },
};

const getCategory = (icon) => {
  return CATEGORIES[icon] || { color: "#667eea", label: "Event" };
};

const TimelineCard = ({ event, side }) => {
  const category = getCategory(event.icon);
  // Use event's image property or fallback to default
  const eventImage = event.image || "/EVENTS/event.png";

  return (
    <article className="tl-card" style={{ "--accent": category.color }}>
      {/* Card Image */}
      <div className="tl-card__image">
        <img src={eventImage} alt={event.title} loading="lazy" />
        <div className="tl-card__image-overlay" />
        <span className="tl-card__category">{category.label}</span>
      </div>

      {/* Card Body */}
      <div className="tl-card__body">
        {/* Time Badge */}
        <div className="tl-card__time">
          <svg
            className="tl-card__time-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
          </svg>
          <span>{event.time}</span>
        </div>

        {event.venue && (
          <div
            className="tl-card__venue"
            aria-label="Venue"
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px",
              marginTop: "8px",
              marginBottom: "12px",
              fontSize: "13px",
              color: "var(--accent)",
              opacity: 0.9,
            }}
          >
            <span
              className="material-icons-outlined"
              aria-hidden="true"
              style={{ fontSize: "16px" }}
            >
              place
            </span>
            <span>{event.venue}</span>
          </div>
        )}

        {/* Title */}
        <h3 className="tl-card__title">{event.title}</h3>

        {/* Description */}
        <p className="tl-card__desc">{event.desc}</p>

        {/* Footer with Icon */}
        <div className="tl-card__footer">
          <div className="tl-card__icon-box">
            <span className="material-icons-outlined">{event.icon}</span>
          </div>
        </div>
      </div>

      {/* Decorative glow effect */}
      <div className="tl-card__glow" aria-hidden="true" />
    </article>
  );
};

/* ============================================
   MAIN TIMELINE COMPONENT
   ============================================ */
const EventTimelinePage = () => {
  const [activeDay, setActiveDay] = useState(1);
  const [isAnimating, setIsAnimating] = useState(false);
  const itemRefs = useRef([]);
  const timelineData = activeDay === 1 ? timelineDay1 : timelineDay2;
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Calculate interesting stats
  const stats = {
    totalEvents: timelineData.length,
    uniqueTimes: new Set(timelineData.map((e) => e.time)).size,
    uniqueCategories: new Set(
      timelineData.map((e) => getCategory(e.icon).label)
    ).size,
    workshops: timelineData.filter((e) => ["build", "school"].includes(e.icon))
      .length,
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* Handle day switch with smooth transition */
  const handleDaySwitch = useCallback(
    (day) => {
      if (day === activeDay || isAnimating) return;

      setIsAnimating(true);

      // Reset visibility before switching
      itemRefs.current.forEach((ref) => {
        if (ref) ref.classList.remove("is-visible");
      });

      setTimeout(() => {
        setActiveDay(day);
        setIsAnimating(false);
        itemRefs.current = [];
        // Scroll to top when switching days
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 350);
    },
    [activeDay, isAnimating]
  );

  /* Intersection Observer for scroll animations */
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: "0px 0px -60px 0px",
      threshold: 0.1,
    };

    const handleIntersect = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const delay = parseInt(entry.target.dataset.index, 10) * 80;
          setTimeout(() => {
            entry.target.classList.add("is-visible");
          }, delay);
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(handleIntersect, observerOptions);

    itemRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [timelineData]);

  return (
    <section className="timeline" id="event-timeline" data-hall="">
      {/* Background Elements */}
      <div className="timeline__bg" aria-hidden="true">
        <div className="timeline__bg-grid" />
        <div className="timeline__bg-glow timeline__bg-glow--1" />
        <div className="timeline__bg-glow timeline__bg-glow--2" />
        <div className="timeline__bg-glow timeline__bg-glow--3" />
        <div className="timeline__bg-stars" />
      </div>

      {/* Header Section */}
      <header className="timeline__header">
        {/*<div className="timeline__badge">
          <span>📅</span>
          <span>Event Schedule</span>
        </div>*/}
        <h1 className="timeline__title">
          <span className="timeline__title-main">Event Timeline</span>
          <span className="timeline__title-sub">CRYPTERA 2K26</span>
        </h1>

        <p className="timeline__intro">
          Two days of innovation, challenges, and discovery await you
        </p>

        {/* Day Selector */}
        <div className="timeline__day-selector">
          <button
            className={`timeline__day-btn ${
              activeDay === 1 ? "is-active" : ""
            }`}
            onClick={() => handleDaySwitch(1)}
            disabled={isAnimating}
          >
            <span className="timeline__day-dot" />
            <div className="timeline__day-info">
              <span className="timeline__day-label">Day 1</span>
              <span className="timeline__day-date">Jan 30, 2026</span>
            </div>
          </button>

          <div className="timeline__day-divider" />

          <button
            className={`timeline__day-btn ${
              activeDay === 2 ? "is-active" : ""
            }`}
            onClick={() => handleDaySwitch(2)}
            disabled={isAnimating}
          >
            <span className="timeline__day-dot" />
            <div className="timeline__day-info">
              <span className="timeline__day-label">Day 2</span>
              <span className="timeline__day-date">Jan 31, 2026</span>
            </div>
          </button>
        </div>
        <br />
        {/* Stats */}
        <div className="timeline__stats">
          <div className="timeline__stat">
            <span className="timeline__stat-value">{stats.totalEvents}</span>
            <span className="timeline__stat-label">Events</span>
          </div>
          <div className="timeline__stat-sep" />
          <div className="timeline__stat">
            <span className="timeline__stat-value">
              {stats.uniqueCategories}
            </span>
            <span className="timeline__stat-label">Categories</span>
          </div>
          <div className="timeline__stat-sep" />
          <div className="timeline__stat">
            <span className="timeline__stat-value">{stats.uniqueTimes}</span>
            <span className="timeline__stat-label">Time Slots</span>
          </div>
        </div>
      </header>

      {/* Timeline Content */}
      <div className={`timeline__content ${isAnimating ? "is-animating" : ""}`}>
        {/* Central Spine */}
        <div className="timeline__spine" aria-hidden="true">
          <div className="timeline__spine-line" />
          <div className="timeline__spine-glow" />
        </div>

        {/* Event List */}
        <div className="timeline__list">
          {timelineData.map((event, index) => {
            const side = index % 2 === 0 ? "left" : "right";
            const category = getCategory(event.icon);

            return (
              <div
                key={`${activeDay}-${index}`}
                ref={(el) => (itemRefs.current[index] = el)}
                data-index={index}
                className={`timeline__item timeline__item--${side}`}
                style={{ "--accent": category.color }}
              >
                {/* Node on spine */}
                <div className="timeline__node">
                  <span className="timeline__node-dot" />
                  <span className="timeline__node-ring" />
                </div>

                {/* Connector line */}
                <div className="timeline__connector" />

                {/* Event Card */}
                <TimelineCard event={event} side={side} />
              </div>
            );
          })}
        </div>

        {/* End Marker */}
        <div className="timeline__end">
          <div className="timeline__end-node">
            <span className="material-icons-outlined">flag</span>
          </div>
          <p className="timeline__end-text">
            {activeDay === 1 ? (
              <button 
                className="timeline__day-switch-link" 
                onClick={() => handleDaySwitch(2)}
              >
                Day 1 Complete — Switch to Day 2 →
              </button>
            ) : (
              "See you at CRYPTERA 2K27! 🚀"
            )}
          </p>
          <button className="timeline__back-to-top" onClick={scrollToTop}>
            <span className="material-icons-outlined">arrow_upward</span>
            <span>Back to Top</span>
          </button>
        </div>
      </div>
    </section>
  );
};

export default EventTimelinePage;
